import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { API_URL } from '../config';
import { format, addMinutes, parseISO } from 'date-fns';
import {
  MapPin, Phone, Clock, CheckCircle2, AlertCircle, ArrowRight,
  ChevronLeft, Loader, MessageCircle, CalendarX2,
} from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { ThemeToggle } from '../components/ui/ThemeToggle';
import { SectionHeading } from '../components/ui/SectionHeading';

interface Provider {
  business_name: string;
  description: string | null;
  category: string | null;
  location: string | null;
  slug: string;
  contact_number: string | null;
}

interface Service {
  id: string;
  name: string;
  description: string | null;
  duration: number;
  price: number;
}

interface ScheduleDay {
  day_of_week: number;
  is_open: boolean;
  open_time: string;
  close_time: string;
}

interface BookingResult {
  bookingId: string;
  providerName: string;
  providerPhone: string | null;
  guestName: string;
  guestPhone: string;
  serviceName: string;
  price: string;
  date: string;
  time: string;
  duration: number;
}

type Step = 'services' | 'datetime' | 'details' | 'submitting' | 'done';

const TODAY = new Date().toISOString().split('T')[0];
const SESSION_KEY = 'nexslot_booking_confirm';

interface BookedSlot { start: string; end: string; }

const toMin = (t: string) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };

// Slots spaced by service duration — a 60-min service shows 09:00, 10:00, 11:00...
const generateAllSlots = (openTime: string, closeTime: string, durationMins: number): string[] => {
  const openMin = toMin(openTime);
  const closeMin = toMin(closeTime);
  const slots: string[] = [];
  let cur = openMin;
  while (cur + durationMins <= closeMin) {
    slots.push(`${Math.floor(cur / 60).toString().padStart(2, '0')}:${(cur % 60).toString().padStart(2, '0')}`);
    cur += durationMins;
  }
  return slots;
};

const isBooked = (slot: string, durationMins: number, booked: BookedSlot[]): boolean => {
  const s = toMin(slot);
  return booked.some(b => s < toMin(b.end) && s + durationMins > toMin(b.start));
};

const fmtTime = (t: string): string => {
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${m.toString().padStart(2, '0')} ${ampm}`;
};

const toWhatsAppNumber = (phone: string): string => {
  const digits = phone.replace(/\D/g, '');
  if (digits.startsWith('60')) return digits;
  if (digits.startsWith('0')) return '6' + digits;
  return '60' + digits;
};

const buildWhatsAppUrl = (result: BookingResult): string => {
  if (!result.providerPhone) return '';
  const number = toWhatsAppNumber(result.providerPhone);
  const endTime = fmtTime(format(
    addMinutes(parseISO(`${result.date}T${result.time}`), result.duration),
    'HH:mm'
  ));
  const dateStr = format(parseISO(result.date), 'EEEE, d MMMM yyyy');
  const ref = result.bookingId.slice(0, 8).toUpperCase();
  const msg = [
    `Hi ${result.providerName}! I just made a booking via NexSlot.`,
    ``,
    `*Service:* ${result.serviceName}`,
    `*Date:* ${dateStr}`,
    `*Time:* ${fmtTime(result.time)} – ${endTime}`,
    `*Name:* ${result.guestName}`,
    `*Phone:* ${result.guestPhone}`,
    `*Reference:* ${ref}`,
    ``,
    `Please confirm my appointment. Thank you! 🙏`,
  ].join('\n');
  return `https://wa.me/${number}?text=${encodeURIComponent(msg)}`;
};

export const StorefrontPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();

  const [provider, setProvider] = useState<Provider | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [schedule, setSchedule] = useState<ScheduleDay[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pageLoading, setPageLoading] = useState(true);

  const [step, setStep] = useState<Step>('services');
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [bookingResult, setBookingResult] = useState<BookingResult | null>(null);
  const [bookedSlots, setBookedSlots] = useState<BookedSlot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);

  useEffect(() => {
    // Restore confirmation from sessionStorage after ToyyibPay redirect
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (stored) {
      try {
        setBookingResult(JSON.parse(stored));
        setStep('done');
      } catch {}
      sessionStorage.removeItem(SESSION_KEY);
    }

    fetch(`${API_URL}/storefront/${slug}`)
      .then(r => r.json())
      .then(data => {
        if (!data.success) throw new Error(data.message);
        setProvider(data.provider);
        setServices(data.services);
        setSchedule(data.schedule ?? []);
      })
      .catch(e => setLoadError(e.message))
      .finally(() => setPageLoading(false));
  }, [slug]);

  const selectService = (svc: Service) => {
    setSelectedService(svc);
    setSelectedDate('');
    setSelectedTime('');
    setStep('datetime');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedService || !selectedDate || !selectedTime || !provider) return;
    setStep('submitting');
    setSubmitError(null);

    try {
      const startDT = new Date(`${selectedDate}T${selectedTime}:00`);
      const endDT = addMinutes(startDT, selectedService.duration);

      const res = await fetch(`${API_URL}/storefront/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          service_id: selectedService.id,
          start_time: startDT.toISOString(),
          end_time: endDT.toISOString(),
          guest_name: guestName,
          guest_email: guestEmail,
          guest_phone: guestPhone,
          frontendUrl: window.location.origin,
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);

      const result: BookingResult = {
        bookingId: data.booking.id,
        providerName: provider.business_name,
        providerPhone: provider.contact_number,
        guestName,
        guestPhone,
        serviceName: selectedService.name,
        price: Number(selectedService.price).toFixed(2),
        date: selectedDate,
        time: selectedTime,
        duration: selectedService.duration,
      };

      if (data.paymentUrl) {
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(result));
        window.location.href = data.paymentUrl;
      } else {
        setBookingResult(result);
        setStep('done');
      }
    } catch (err: any) {
      setSubmitError(err.message);
      setStep('details');
    }
  };

  if (pageLoading) return (
    <div className="min-h-screen bg-surface flex items-center justify-center">
      <Loader size={32} className="animate-spin text-ink-faint" />
    </div>
  );

  if (loadError || !provider) return (
    <div className="min-h-screen bg-surface flex flex-col items-center justify-center gap-4 px-6 text-center">
      <AlertCircle size={48} className="text-ink-faint" />
      <p className="display text-2xl text-ink">Storefront not found</p>
      <p className="label-caps text-ink-faint">{loadError}</p>
    </div>
  );

  // Confirmation screen
  if (step === 'done' && bookingResult) {
    const ref = bookingResult.bookingId.slice(0, 8).toUpperCase();
    const endTime = fmtTime(format(
      addMinutes(parseISO(`${bookingResult.date}T${bookingResult.time}`), bookingResult.duration),
      'HH:mm'
    ));
    const waUrl = buildWhatsAppUrl(bookingResult);

    return (
      <div className="min-h-screen bg-surface flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-md space-y-6">
          <div className="text-center">
            <div className="w-20 h-20 bg-green-100 dark:bg-green-900/40 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 size={40} className="text-green-600 dark:text-green-400" />
            </div>
            <h2 className="display text-3xl text-ink">Booking received</h2>
            <p className="text-ink-muted text-sm mt-2">Your slot has been reserved. Notify the provider below.</p>
          </div>

          <div className="bg-inverse rounded-sm p-6 text-center border border-line">
            <p className="label-caps text-slate-400">Booking reference</p>
            <p className="text-3xl font-mono font-semibold text-accent tracking-widest mt-2">{ref}</p>
          </div>

          <Card className="divide-y divide-line">
            {[
              ['Service', bookingResult.serviceName],
              ['Date', format(parseISO(bookingResult.date), 'EEE, d MMM yyyy')],
              ['Time', `${fmtTime(bookingResult.time)} – ${endTime}`],
              ['Provider', bookingResult.providerName],
            ].map(([label, value]) => (
              <div key={label} className="flex justify-between items-center px-6 py-4">
                <span className="label-caps text-ink-faint">{label}</span>
                <span className="text-sm font-semibold text-ink">{value}</span>
              </div>
            ))}
            <div className="flex justify-between items-center px-6 py-4">
              <span className="label-caps text-ink-faint">Amount</span>
              <span className="display text-lg text-accent">RM {bookingResult.price}</span>
            </div>
          </Card>

          {waUrl ? (
            <a
              href={waUrl}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center gap-3 w-full bg-[#25D366] text-white min-h-[48px] rounded-sm label-caps hover:bg-[#1ebe5d] transition-colors"
            >
              <MessageCircle size={18} />
              Notify provider via WhatsApp
            </a>
          ) : (
            <p className="text-center label-caps text-ink-faint py-2">
              Provider contact not available — please call to confirm
            </p>
          )}

          <button
            onClick={() => {
              setStep('services');
              setSelectedService(null);
              setBookingResult(null);
              setGuestName(''); setGuestEmail(''); setGuestPhone('');
              setSelectedDate(''); setSelectedTime('');
            }}
            className="w-full min-h-[48px] border border-line-strong text-ink-muted label-caps rounded-sm hover:border-ink hover:text-ink transition-colors"
          >
            Make another booking
          </button>
        </div>
      </div>
    );
  }

  const progressStep = step === 'services' ? 1 : step === 'datetime' ? 2 : 3;
  const stepLabel = ['Choose a service', 'Pick a date & time', 'Your details'][progressStep - 1];
  const priceLabel = selectedService ? `RM ${Number(selectedService.price).toFixed(2)}` : '';
  const showMobileCta =
    (step === 'datetime' && !!selectedDate && !!selectedTime) ||
    step === 'details' || step === 'submitting';

  return (
    <div className="min-h-screen bg-surface pb-24 md:pb-0">
      {/* Provider hero — navy in both modes */}
      <header className="bg-inverse text-ink-inverse">
        <div className="max-w-5xl mx-auto px-6 py-10 md:py-16">
          <div className="flex items-start justify-between gap-4">
            <div>
              {provider.category && (
                <p className="label-caps text-accent mb-3">{provider.category}</p>
              )}
              <h1 className="display text-3xl md:text-5xl leading-tight">
                {provider.business_name}
              </h1>
              {provider.description && (
                <p className="text-slate-300 mt-4 max-w-xl leading-relaxed text-sm">{provider.description}</p>
              )}
              <div className="flex flex-wrap gap-x-6 gap-y-2 mt-6 label-caps text-slate-400">
                {provider.location && (
                  <span className="flex items-center gap-2">
                    <MapPin size={13} className="text-accent" /> {provider.location}
                  </span>
                )}
                {provider.contact_number && (
                  <span className="flex items-center gap-2">
                    <Phone size={13} className="text-accent" /> {provider.contact_number}
                  </span>
                )}
              </div>
            </div>
            <ThemeToggle onInverse className="shrink-0" />
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10 md:py-12">
        {/* Editorial step indicator */}
        <div className="mb-10 flex items-center gap-4">
          {step !== 'services' && step !== 'submitting' && (
            <button
              onClick={() => setStep(step === 'details' ? 'datetime' : 'services')}
              className="flex items-center gap-1 label-caps text-ink-faint hover:text-ink transition-colors min-h-[44px]"
            >
              <ChevronLeft size={16} /> Back
            </button>
          )}
          <span className="label-caps text-ink-faint whitespace-nowrap">Step {progressStep} of 3</span>
          <span className="h-px flex-1 bg-line" />
          <span className="label-caps text-ink-muted whitespace-nowrap hidden sm:block">{stepLabel}</span>
        </div>

        {/* Step 1 — Service selection */}
        {step === 'services' && (
          <div className="space-y-6">
            {services.length === 0 ? (
              <div className="py-16 px-6 flex flex-col items-center text-center border border-dashed border-line rounded-sm">
                <CalendarX2 size={40} className="text-ink-faint mb-4" />
                <p className="display text-lg text-ink-muted">No services available</p>
                <p className="label-caps text-ink-faint mt-2">Check back soon</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                {services.map(svc => (
                  <button key={svc.id} onClick={() => selectService(svc)} className="text-left group">
                    <Card accent className="p-6 md:p-8 h-full transition-colors group-hover:border-line-strong">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <h3 className="display text-xl text-ink">{svc.name}</h3>
                          {svc.description && (
                            <p className="text-xs text-ink-muted mt-2 leading-relaxed">{svc.description}</p>
                          )}
                          <span className="flex items-center gap-1.5 label-caps text-ink-faint mt-4">
                            <Clock size={12} /> {svc.duration} min
                          </span>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="display text-2xl text-ink">RM {Number(svc.price).toFixed(2)}</p>
                          <span className="mt-3 inline-flex items-center gap-1 label-caps text-accent group-hover:gap-2 transition-all">
                            Book <ArrowRight size={12} />
                          </span>
                        </div>
                      </div>
                    </Card>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 2 — Date & time */}
        {step === 'datetime' && selectedService && (
          <div className="max-w-lg mx-auto space-y-6">
            <Card accent className="p-6 flex items-center justify-between">
              <div>
                <p className="label-caps text-ink-faint">Selected service</p>
                <p className="display text-lg text-ink mt-1">{selectedService.name}</p>
              </div>
              <div className="text-right">
                <p className="display text-2xl text-ink">RM {Number(selectedService.price).toFixed(2)}</p>
                <p className="label-caps text-ink-faint mt-1">{selectedService.duration} min</p>
              </div>
            </Card>

            <Card className="p-6 md:p-8 space-y-6">
              <SectionHeading>Select date &amp; time</SectionHeading>
              <Input
                label="Date"
                type="date"
                min={TODAY}
                value={selectedDate}
                onChange={e => {
                  const date = e.target.value;
                  setSelectedDate(date);
                  setSelectedTime('');
                  setBookedSlots([]);
                  if (date && selectedService) {
                    setSlotsLoading(true);
                    fetch(`${API_URL}/storefront/${slug}/availability?service_id=${selectedService.id}&date=${date}`)
                      .then(r => r.json())
                      .then(d => { if (d.success) setBookedSlots(d.bookedSlots); })
                      .catch(() => {})
                      .finally(() => setSlotsLoading(false));
                  }
                }}
              />
              {selectedDate && (() => {
                const dow = new Date(selectedDate + 'T00:00:00').getDay();
                const daySchedule = schedule.find(s => s.day_of_week === dow);
                if (!daySchedule || !daySchedule.is_open) return (
                  <div className="py-6 text-center border border-dashed border-line rounded-sm">
                    <p className="display text-base text-ink-muted">Closed on this day</p>
                    <p className="label-caps text-ink-faint mt-2">Please select another date</p>
                  </div>
                );
                const slots = generateAllSlots(daySchedule.open_time, daySchedule.close_time, selectedService.duration);
                return (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="label-caps text-ink-muted">Time</span>
                      <span className="label-caps text-ink-faint">
                        {fmtTime(daySchedule.open_time)} – {fmtTime(daySchedule.close_time)}
                      </span>
                    </div>
                    {slotsLoading ? (
                      <div className="py-6 flex items-center justify-center gap-2 text-ink-faint">
                        <Loader size={14} className="animate-spin" />
                        <span className="label-caps">Checking availability...</span>
                      </div>
                    ) : slots.length === 0 ? (
                      <div className="py-4 text-center border border-dashed border-line rounded-sm">
                        <p className="label-caps text-ink-faint">
                          No slots fit within working hours for this service
                        </p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-3 gap-2 max-h-72 overflow-y-auto pr-1">
                        {slots.map(slot => {
                          const taken = isBooked(slot, selectedService.duration, bookedSlots);
                          return (
                            <button
                              key={slot}
                              type="button"
                              disabled={taken}
                              onClick={() => setSelectedTime(slot)}
                              className={`min-h-[44px] px-2 rounded-sm text-center border transition-colors text-xs font-semibold ${
                                taken
                                  ? 'bg-surface text-ink-faint border-line line-through cursor-not-allowed'
                                  : selectedTime === slot
                                    ? 'bg-inverse text-accent border-inverse dark:bg-accent dark:text-accent-contrast dark:border-accent'
                                    : 'bg-raised text-ink border-line-strong hover:border-ink'
                              }`}
                            >
                              {fmtTime(slot)}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {selectedTime && (
                      <p className="label-caps text-ink-muted">
                        Session: {fmtTime(selectedTime)} – {fmtTime(format(addMinutes(parseISO(`${selectedDate}T${selectedTime}`), selectedService.duration), 'HH:mm'))}
                      </p>
                    )}
                  </div>
                );
              })()}
              <button
                type="button"
                disabled={!selectedDate || !selectedTime}
                onClick={() => setStep('details')}
                className="hidden md:flex w-full items-center justify-center gap-3 bg-inverse text-ink-inverse dark:bg-accent dark:text-accent-contrast min-h-[48px] rounded-sm label-caps hover:opacity-90 transition-opacity disabled:opacity-30 disabled:cursor-not-allowed"
              >
                Continue <ArrowRight size={16} />
              </button>
            </Card>
          </div>
        )}

        {/* Step 3 — Guest details */}
        {(step === 'details' || step === 'submitting') && selectedService && (
          <div className="max-w-lg mx-auto space-y-6">
            <div className="bg-inverse text-ink-inverse rounded-sm p-6 space-y-3 border border-line">
              <p className="label-caps text-slate-400">Booking summary</p>
              <div className="flex justify-between items-start gap-4">
                <div>
                  <p className="display text-xl">{selectedService.name}</p>
                  <p className="text-slate-400 text-xs mt-1">
                    {selectedDate && format(parseISO(selectedDate), 'EEEE, d MMMM yyyy')} · {fmtTime(selectedTime)}
                    {selectedTime && ` – ${fmtTime(format(addMinutes(parseISO(`${selectedDate}T${selectedTime}`), selectedService.duration), 'HH:mm'))}`}
                  </p>
                </div>
                <p className="display text-2xl text-accent shrink-0">RM {Number(selectedService.price).toFixed(2)}</p>
              </div>
            </div>

            {submitError && (
              <div className="p-4 flex items-center gap-3 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 rounded-sm border border-red-200 dark:border-red-900 text-xs font-semibold">
                <AlertCircle size={16} className="shrink-0" /> {submitError}
              </div>
            )}

            <form id="booking-details-form" onSubmit={handleSubmit}>
              <Card className="p-6 md:p-8 space-y-5">
                <SectionHeading>Your details</SectionHeading>
                <Input required label="Full name" type="text" value={guestName}
                  onChange={e => setGuestName(e.target.value)} placeholder="Your full name" />
                <Input required label="Email" type="email" value={guestEmail}
                  onChange={e => setGuestEmail(e.target.value)} placeholder="you@example.com" />
                <Input required label="Phone number" type="tel" value={guestPhone}
                  onChange={e => setGuestPhone(e.target.value)} placeholder="+60 12-345 6789" />
                <button
                  type="submit"
                  disabled={step === 'submitting'}
                  className="hidden md:flex w-full items-center justify-center gap-3 bg-inverse text-ink-inverse dark:bg-accent dark:text-accent-contrast min-h-[52px] rounded-sm label-caps hover:opacity-90 transition-opacity disabled:opacity-40"
                >
                  {step === 'submitting'
                    ? <><Loader size={16} className="animate-spin" /> Processing...</>
                    : <>Confirm &amp; pay {priceLabel} <ArrowRight size={16} /></>}
                </button>
                <p className="text-center label-caps text-ink-faint">
                  Secured by ToyyibPay · You will be redirected to complete payment
                </p>
              </Card>
            </form>
          </div>
        )}
      </main>

      {/* Mobile sticky CTA — thumb zone */}
      {showMobileCta && (
        <div className="fixed bottom-0 inset-x-0 md:hidden bg-inverse border-t border-line p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] z-30">
          {step === 'datetime' ? (
            <button
              type="button"
              onClick={() => setStep('details')}
              className="w-full flex items-center justify-center gap-3 bg-accent text-accent-contrast min-h-[48px] rounded-sm label-caps"
            >
              Continue · {priceLabel} <ArrowRight size={16} />
            </button>
          ) : (
            <button
              type="submit"
              form="booking-details-form"
              disabled={step === 'submitting'}
              className="w-full flex items-center justify-center gap-3 bg-accent text-accent-contrast min-h-[48px] rounded-sm label-caps disabled:opacity-40"
            >
              {step === 'submitting'
                ? <><Loader size={16} className="animate-spin" /> Processing...</>
                : <>Reserve · {priceLabel}</>}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
